import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Mail, Lock, Eye, EyeOff, Shield, Users, BookOpen, Heart } from 'lucide-react';

export default function AdminLogin() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const { login, loading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const result = await login(form.email, form.password);
    if (result.success) navigate('/admin');
    else setError(result.message || 'Invalid credentials');
  };

  return (
    <div className="min-h-screen flex font-body">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-hero-gradient flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIwMjA2MCIgc3Ryb2tlLXdpZHRoPSIwLjUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')]" />
        <div className="absolute top-0 right-0 w-72 h-72 bg-gold-500/10 rounded-full blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-12">
            <img src="/logo.jpeg" alt="SAAI" className="w-12 h-12 rounded-full object-cover ring-2 ring-gold-400/40" />
            <div>
              <div className="text-white font-display font-bold text-xl">SAAI Institute</div>
              <div className="text-gold-400 text-xs uppercase tracking-widest">Admin Portal</div>
            </div>
          </div>
          <h1 className="font-display text-4xl font-bold text-white mb-4 leading-tight">
            Managing Impact<br /><span className="text-gold-400 italic font-light">at Scale.</span>
          </h1>
          <p className="text-white/50 leading-relaxed max-w-sm">
            The SAAI admin dashboard gives you full control over content, donations, programs, and community engagement — all in one place.
          </p>
        </div>
        <div>
          <blockquote className="border-l-2 border-gold-400 pl-4 mb-8">
            <p className="text-white/70 italic text-sm">"The measure of a life is not its duration, but its donation."</p>
            <cite className="text-gold-400 text-xs mt-1 block">— Peter Marshall</cite>
          </blockquote>
          <div className="grid grid-cols-3 gap-4">
            {[
              { icon: Heart, v: '15K+', l: 'Lives Impacted' },
              { icon: BookOpen, v: '50+', l: 'Programs' },
              { icon: Users, v: '500+', l: 'Volunteers' },
            ].map(s => (
              <div key={s.l} className="glass rounded-xl p-4 text-center">
                <s.icon size={20} className="text-gold-400 mx-auto mb-2" />
                <div className="font-display font-bold text-xl text-white">{s.v}</div>
                <div className="text-white/40 text-xs">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/logo.jpeg" alt="SAAI" className="w-8 h-8 rounded-full object-cover" />
            <span className="font-display font-bold text-royal-900">SAAI Institute</span>
          </div>

          <div className="mb-8">
            <div className="w-12 h-12 bg-royal-800 rounded-2xl flex items-center justify-center mb-4">
              <Shield size={22} className="text-white" />
            </div>
            <h2 className="font-display text-2xl font-bold text-gray-900">Welcome back</h2>
            <p className="text-gray-500 text-sm mt-1">Sign in to your admin account</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-5 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Email</label>
              <div className="relative">
                <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all bg-white"
                  placeholder="admin@saaingo.org" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type={show ? 'text' : 'password'} value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} required
                  className="w-full pl-10 pr-11 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all bg-white"
                  placeholder="••••••••" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {show ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full py-3 bg-royal-800 hover:bg-royal-900 text-white font-semibold rounded-xl text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Signing in...</> : 'Sign In'}
            </button>
          </form>

          {/* <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-xs text-amber-700 font-medium mb-1">Default credentials</p>
            <p className="text-xs text-amber-600 font-mono">admin@saaingo.org / Admin@1234</p>
            <p className="text-xs text-amber-500 mt-1">First visit <code className="bg-amber-100 px-1 rounded">/api/auth/seed</code> to create account</p>
          </div> */}
        </div>
      </div>
    </div>
  );
}
